// src/pages/Reports/TimeAndAttendanceReport.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import Select from "../../components/ReportSelect";
import { saveAs } from "file-saver";
import { Container, Table, Button, Row, Col, Spinner } from "react-bootstrap";
import Pagination from "../../components/Pagination";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import "../../styles/Reports.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "");
const toISO = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "");

const overlaps = (from, to, fStart, fEnd) => {
  if (!fStart && !fEnd) return true;
  const a1 = dayjs(from);
  const a2 = to ? dayjs(to) : a1;
  const b1 = fStart ? dayjs(fStart) : null;
  const b2 = fEnd ? dayjs(fEnd) : null;

  if (b1 && b2) return !(a2.isBefore(b1, "day") || a1.isAfter(b2, "day"));
  if (b1) return !a2.isBefore(b1, "day");
  if (b2) return !a1.isAfter(b2, "day");
  return true;
};

export default function TimeAndAttendanceReport() {
  const navigate = useNavigate();

  const [vacRows, setVacRows] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const [pStartDate, setPStartDate] = useState("");
  const [pEndDate, setPEndDate] = useState("");
  const [pSelOrgUnits, setPSelOrgUnits] = useState([]);
  const [pSelAssociates, setPSelAssociates] = useState([]);
  const [pSelTypes, setPSelTypes] = useState([]);
  const [pSearch, setPSearch] = useState("");

  const [fStartDate, setFStartDate] = useState("");
  const [fEndDate, setFEndDate] = useState("");
  const [fOrgUnitIds, setFOrgUnitIds] = useState([]);
  const [fAssociateIds, setFAssociateIds] = useState([]);
  const [fTypeIds, setFTypeIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchDropdowns = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [ou, us, types] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/users`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/vacation-type`, { headers }),
        ]);
        setOrgUnits(ou.data || []);
        setUsers(us.data || []);
        setLeaveTypes(types.data || []);
      } catch (e) {
        console.error("T&A dropdown fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDropdowns();
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    setHasSearched(true);
    setCurrentPage(1);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const vac = await axios.get(`${BASE_URL}/api/vacationform/admin/all`, {
        headers,
      });
      setVacRows(vac.data || []);
    } catch (e) {
      console.error("T&A rows fetch error:", e);
      setVacRows([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setFStartDate(pStartDate);
    setFEndDate(pEndDate);
    setFOrgUnitIds(pSelOrgUnits.map((o) => o.value));
    setFAssociateIds(pSelAssociates.map((a) => a.value));
    setFTypeIds(pSelTypes.map((t) => t.value));
    setCurrentPage(1);
    fetchRows();
  };

  const clearFilters = () => {
    setPStartDate("");
    setPEndDate("");
    setPSelOrgUnits([]);
    setPSelAssociates([]);
    setPSelTypes([]);
    setPSearch("");
    setFStartDate("");
    setFEndDate("");
    setFOrgUnitIds([]);
    setFAssociateIds([]);
    setFTypeIds([]);
    setVacRows([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  const ouById = {};
  const ouIdByName = {};
  orgUnits.forEach((ou) => {
    ouById[Number(ou.ou_id)] = String(ou.ou_name);
    ouIdByName[String(ou.ou_name).trim()] = Number(ou.ou_id);
  });

  const userById = {};
  users.forEach((u) => {
    userById[Number(u.id)] = u;
  });

  const typeNameById = {};
  leaveTypes.forEach((t) => {
    const id = Number(t.vt_id ?? t.id ?? t.value);
    const name = t.vt_name || t.vt_type || t.name || String(id);
    if (!Number.isNaN(id)) typeNameById[id] = name;
  });

  const resolveUserOuId = (u) => {
    if (!u?.org_unit) return null;
    const raw = u.org_unit;
    const asNum = Number(raw);
    if (!Number.isNaN(asNum)) return asNum;
    return ouIdByName[String(raw).trim()] ?? null;
  };

  const filtered = vacRows.filter((r) => {
    const u = userById[Number(r.vf_userid)] || {};
    const ouId = resolveUserOuId(u);
    const typeId = Number(r.vf_type);

    const passDate = overlaps(
      r.vf_from_date,
      r.vf_to_date,
      fStartDate,
      fEndDate
    );
    const passOU =
      fOrgUnitIds.length === 0 || (ouId && fOrgUnitIds.includes(ouId));
    const passUser =
      fAssociateIds.length === 0 || fAssociateIds.includes(Number(r.vf_userid));
    const passType =
      fTypeIds.length === 0 ||
      (!Number.isNaN(typeId) && fTypeIds.includes(typeId));
    const search = pSearch.trim().toLocaleLowerCase();
    const passSearch =
      !search ||
      [
        r.vf_id,
        r.vf_userid,
        u.userid_no,
        u.first_name,
        u.last_name,
        ouId ? ouById[ouId] : "",
        typeNameById[typeId],
      ].some((value) =>
        String(value ?? "").toLocaleLowerCase().includes(search)
      );

    return passDate && passOU && passUser && passType && passSearch;
  });

  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const orgUnitOptions = orgUnits.map((o) => ({
    value: Number(o.ou_id),
    label: o.ou_name,
  }));

  const associateOptions = users
    .map((u) => ({
      value: Number(u.id),
      label:
        `${u.first_name || ""} ${u.last_name || ""}`.trim() || `ID ${u.id}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const leaveTypeOptions = Object.entries(typeNameById).map(([id, name]) => ({
    value: Number(id),
    label: name,
  }));

  const exportCSV = () => {
    if (!hasSearched || filtered.length === 0) {
      alert("No data to export");
      return;
    }
    const headers = [
      "Org Unit",
      "Leave Type",
      "First Name",
      "Last Name",
      "Start Date",
      "End Date",
      "User ID",
    ];
    const lines = filtered.map((r) => {
      const u = userById[Number(r.vf_userid)] || {};
      const ouId = resolveUserOuId(u);
      const ouName = ouId ? ouById[ouId] || "" : "";
      const typeName = typeNameById[Number(r.vf_type)] || "";
      const cols = [
        ouName,
        typeName,
        u.first_name || "",
        u.last_name || "",
        fmt(r.vf_from_date),
        fmt(r.vf_to_date),
        r.vf_userid || "",
      ];
      return cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, "TimeAndAttendanceReport.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-clock" /></span><span>Time And Attendance</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      <ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Start Date</label>
          <input
            type="date"
            value={pStartDate || ""}
            onChange={(e) => setPStartDate(toISO(e.target.value))}
            className="form-control"
          />
        </Col>
        <Col>
          <label>End Date</label>
          <input
            type="date"
            value={pEndDate || ""}
            onChange={(e) => setPEndDate(toISO(e.target.value))}
            className="form-control"
          />
        </Col>
        <Col>
          <label>Org Units</label>
          <Select
            isMulti
            options={orgUnitOptions}
            value={pSelOrgUnits}
            onChange={setPSelOrgUnits}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col>
          <label>Associates</label>
          <Select
            isMulti
            options={associateOptions}
            value={pSelAssociates}
            onChange={setPSelAssociates}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
        <Col>
          <label>Leave Types</label>
          <Select
            isMulti
            options={leaveTypeOptions}
            value={pSelTypes}
            onChange={setPSelTypes}
            classNamePrefix="rs"
            placeholder="Select..."
          />
        </Col>
      </Row>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search</label>
          <input type="text" className="form-control" placeholder="ID / Name / Keyword" value={pSearch} onChange={(event) => setPSearch(event.target.value)} />
        </Col>
      </Row>
      </ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto" className="align-end">
          <div className="button-group">
            <Button variant="primary" onClick={applyFilters}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>
        <Col md="auto" className="align-end ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || filtered.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                <th>Org Unit</th>
                <th>Leave Type</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Start Date</th>
                <th>End Date</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paginatedData.map((r) => {
                  const u = userById[Number(r.vf_userid)] || {};
                  const ouId = resolveUserOuId(u);
                  const ouName = ouId ? ouById[ouId] || "" : "";
                  const typeName = typeNameById[Number(r.vf_type)] || "";
                  return (
                    <tr key={r.vf_id}>
                      <td className="text-center">{ouName}</td>
                      <td className="text-center">{typeName}</td>
                      <td className="text-center">{u.first_name || ""}</td>
                      <td className="text-center">{u.last_name || ""}</td>
                      <td className="text-center">{fmt(r.vf_from_date)}</td>
                      <td className="text-center">
                        {r.vf_to_date ? fmt(r.vf_to_date) : ""}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </Container>
  );
}
